import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, TriangleAlert } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getCatalog,
  getDocument,
  type DocumentResult,
  type DocumentView,
  type FacsimileRef,
  type Licence,
  type Provenance,
} from '../lib/library';
import { resolveWikilinks } from '../lib/wikilinks';

type Load = { kind: 'loading' } | DocumentResult;

/**
 * Spec 008 — the heritage document reading view (SC-040/044/045/048). Renders the verbatim Swedish markdown
 * (headings, GFM tables, bilingual DE/SV blockquotes, images, resolved wikilinks) in an editorial serif
 * column, then an archival colophon: provenance, the dual licence, and links to the external facsimile
 * prints. Raw HTML in the body is NOT rendered (react-markdown default) — embedded scripts are inert.
 * Four states: loading → success → not-found → unavailable/error.
 */
export default function LibraryDocument() {
  const { slug = '' } = useParams();
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [knownSlugs, setKnownSlugs] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let active = true;
    setState({ kind: 'loading' });
    // Fetch the catalog (for wikilink resolution) and the document together; the doc drives the view, the
    // catalog only supplies the allowlist of slugs a [[wikilink]] may resolve to.
    Promise.all([getCatalog(), getDocument(slug)]).then(([cat, doc]) => {
      if (!active) return;
      if (cat.kind === 'ok') {
        setKnownSlugs(new Set([...cat.data.thematic, ...cat.data.primary].map((e) => e.slug)));
      }
      setState(doc);
    });
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <article className="mx-auto w-full max-w-prose px-6 py-16 sm:py-20">
      <Link
        to="/library"
        className="inline-flex items-center gap-1.5 rounded font-sans text-sm text-muted transition-colors hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" />
        Back to the archive
      </Link>

      {state.kind === 'loading' && (
        <div
          className="mt-10 flex items-center gap-3 font-sans text-sm text-muted"
          role="status"
          aria-live="polite"
        >
          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          Loading the document…
        </div>
      )}

      {state.kind === 'notfound' && (
        <Notice
          heading="Document not found"
          body="There’s no document at this address. It may have been renamed. Return to the archive to find it."
        />
      )}
      {state.kind === 'unavailable' && (
        <Notice
          heading="The archive is not available"
          body="The heritage archive isn’t reachable right now. Nothing else on the site is affected — try again shortly."
        />
      )}
      {state.kind === 'error' && (
        <Notice
          heading="Couldn’t load the document"
          body="We couldn’t reach the server. Check your connection and try again."
        />
      )}

      {state.kind === 'ok' && <Reader doc={state.data} knownSlugs={knownSlugs} />}
    </article>
  );
}

function Notice({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="mt-10 flex items-start gap-3 rounded-lg border border-border bg-surface p-6" role="alert">
      <TriangleAlert aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-accent-deep" />
      <div>
        <h1 className="font-display text-sm font-semibold uppercase tracking-[0.12em] text-ink">{heading}</h1>
        <p className="mt-2 font-serif text-lg leading-relaxed text-ink">{body}</p>
      </div>
    </div>
  );
}

function Reader({ doc, knownSlugs }: { doc: DocumentView; knownSlugs: ReadonlySet<string> }) {
  // Resolve [[wikilinks]] against the catalog allowlist before rendering (memoised on body/slugs).
  const body = useMemo(() => resolveWikilinks(doc.bodyMarkdown, knownSlugs), [doc.bodyMarkdown, knownSlugs]);
  const components = useMemo(() => markdownComponents(), []);

  return (
    <>
      <p className="mt-10 font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">
        {doc.group === 'primary' ? 'Primary source' : 'Thematic document'}
      </p>
      <div className="heritage-body mt-4">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {body}
        </ReactMarkdown>
      </div>

      <Colophon provenance={doc.provenance} licence={doc.licence} facsimiles={doc.facsimiles} />
    </>
  );
}

/** The archival colophon — provenance + dual licence + external facsimile prints, styled like a book imprint. */
function Colophon({
  provenance,
  licence,
  facsimiles,
}: {
  provenance: Provenance;
  licence: Licence;
  facsimiles: FacsimileRef[];
}) {
  return (
    <aside className="mt-16 border-t border-border pt-8" aria-label="Provenance, licence and sources">
      <section>
        <h2 className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-accent-deep">
          Provenance
        </h2>
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 font-sans text-sm sm:grid-cols-[10rem_1fr]">
          <Row label="Source repository" value={provenance.sourceRepository} />
          <Row label="Source commit" value={provenance.sourceCommit} mono />
          <Row label="Commit date" value={provenance.commitDate} />
          <Row label="Method" value={provenance.method} />
          <Row label="Archived" value={provenance.archivedOn} />
          <Row label="Compiler" value={provenance.compiler} />
        </dl>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-accent-deep">
          Licence
        </h2>
        <p className="mt-3 font-serif text-base leading-relaxed text-ink">
          {licence.textTranslationCompilation}
        </p>
        <p className="mt-2 font-serif text-base leading-relaxed text-ink">{licence.primarySourcesAndImages}</p>
      </section>

      {facsimiles.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-accent-deep">
            Facsimile source prints
          </h2>
          <p className="mt-2 font-sans text-sm text-muted">
            The digitised period prints this material rests on. They’re large, so they live as downloadable
            release assets — verify each against its checksum.
          </p>
          <ul className="mt-4 space-y-4">
            {facsimiles.map((f) => (
              <li key={f.filename} className="border-l-2 border-border pl-4">
                <a
                  href={f.assetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded font-serif text-lg text-ink underline decoration-accent decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  {f.title}
                  <ExternalLink aria-hidden className="h-3.5 w-3.5 text-accent-deep" />
                </a>
                <p className="mt-1 font-sans text-xs text-muted">
                  <span className="font-semibold uppercase tracking-wide">{f.sizeLabel}</span>
                  {' · SHA-256 '}
                  <span className="break-all font-mono text-[0.7rem] text-muted">{f.sha256}</span>
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="font-semibold uppercase tracking-wide text-accent-deep">{label}</dt>
      <dd className={`text-ink ${mono ? 'break-all font-mono text-xs' : ''}`}>{value}</dd>
    </>
  );
}

/**
 * react-markdown element → tokened component map. Internal `/library/...` links become router <Link>s;
 * every other link opens externally. Image `src` is rewritten to the allowlisted asset route by basename.
 * No `rehype-raw`, so raw HTML/scripts in the body never render (SC-048).
 */
function markdownComponents(): Components {
  return {
    h1: ({ children }) => (
      <h1 className="mt-2 font-display text-3xl font-semibold uppercase tracking-[0.1em] text-ink sm:text-4xl">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mt-10 font-display text-xl font-semibold uppercase tracking-[0.08em] text-ink">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-8 font-serif text-2xl font-semibold text-ink">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="mt-6 font-sans text-sm font-semibold uppercase tracking-wide text-accent-deep">
        {children}
      </h4>
    ),
    p: ({ children }) => <p className="mt-4 font-serif text-lg leading-relaxed text-ink">{children}</p>,
    ul: ({ children }) => (
      <ul className="mt-4 list-disc space-y-2 pl-6 font-serif text-lg leading-relaxed text-ink">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="mt-4 list-decimal space-y-2 pl-6 font-serif text-lg leading-relaxed text-ink">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="pl-1">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="mt-5 border-l-2 border-accent/60 bg-surface/60 py-1 pl-4 font-serif text-lg italic leading-relaxed text-ink">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-10 border-border" />,
    strong: ({ children }) => <strong className="font-semibold not-italic text-ink">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-[0.85em] text-accent-deep">
        {children}
      </code>
    ),
    a: ({ href, children }) => {
      const to = href ?? '';
      if (to.startsWith('/library/')) {
        return (
          <Link
            to={to}
            className="text-ink underline decoration-accent decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {children}
          </Link>
        );
      }
      return (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink underline decoration-accent decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {children}
        </a>
      );
    },
    img: ({ src, alt }) => {
      const raw = typeof src === 'string' ? src : '';
      // Rewrite a relative archive image (e.g. "bilder/x.jpg") to the allowlisted asset route by basename.
      const external = /^https?:\/\//i.test(raw);
      const file = raw.split('/').pop() ?? '';
      const resolved = external ? raw : `/api/library/asset/${encodeURIComponent(file)}`;
      return (
        <img
          src={resolved}
          alt={alt ?? ''}
          loading="lazy"
          className="mt-6 h-auto max-w-full rounded-md border border-border"
        />
      );
    },
    table: ({ children }) => (
      <div className="mt-6 overflow-x-auto">
        <table className="w-full border-collapse font-sans text-sm text-ink">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="border-b border-border text-left">{children}</thead>,
    th: ({ children }) => (
      <th className="px-3 py-2 font-semibold uppercase tracking-wide text-accent-deep">{children}</th>
    ),
    td: ({ children }) => <td className="border-b border-border px-3 py-2 align-top">{children}</td>,
  };
}
